import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SessionService } from '../services/session.service';
import { AuthService } from '../services/auth.service';

export const guestGuard: CanActivateFn = async (route, state) => {
  const sessionService = inject(SessionService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const currentUrl = state.url;

  // Wait for Supabase to restore any persisted session before deciding. Without
  // this, a logged-in user briefly sees the login/signup/welcome page on cold
  // start: the backend UserSession lives in sessionStorage (empty on app
  // reopen / hard reload) and is rebuilt async, while the authoritative Supabase
  // session hasn't finished restoring yet — so the guard wrongly treats the user
  // as a guest and renders the auth page (the "flash" before getting bounced in).
  await authService.waitUntilReady();

  let hasBackendSession = !!sessionService.session?.token;
  const hasSupabaseSession = !!authService.session$.value;

  console.log(
    'guestGuard: checking route',
    currentUrl,
    'backendSession:',
    hasBackendSession,
    'supabaseSession:',
    hasSupabaseSession,
  );

  // Stale cache: an app session persisted on this device but Supabase reports no
  // session (signed out / refresh token expired). Drop it and treat the user as
  // a guest so the auth page renders, instead of bouncing through home.
  if (hasBackendSession && !hasSupabaseSession) {
    console.log('guestGuard: stale cached session, Supabase signed out — evicting');
    sessionService.clearPersisted();
    hasBackendSession = false;
  }

  if (!hasBackendSession && !hasSupabaseSession) {
    console.log('guestGuard: user not authenticated, allowing access to guest page');
    return true;
  }

  const lang = route.params['lang'] || 'en';

  // Authenticated via Supabase but the backend UserSession isn't rebuilt yet —
  // send to the loading spinner, which routes onward (and consumes any stored
  // deep link) once AppComponent.wireAuth() finishes building the app session.
  // Don't consume the redirectUrl here: we can't land it without a token, and
  // stealing it would deprive the spinner of the deep link.
  if (!hasBackendSession) {
    console.log('guestGuard: session restoring, routing to loading spinner');
    return router.createUrlTree([lang, 'app', 'loading']);
  }

  const redirectUrl = sessionService.consumeRedirectUrl();

  if (redirectUrl) {
    const urlSegments = redirectUrl.split('/').filter((s) => s);
    if (urlSegments[0] === 'en' || urlSegments[0] === 'he') {
      urlSegments.shift();
    }

    const [pathPart, queryPart] = urlSegments.join('/').split('?');
    const pathSegments = pathPart.split('/').filter((s) => s);
    const urlTree = router.createUrlTree([lang, ...pathSegments]);

    if (queryPart) {
      const params: any = {};
      queryPart.split('&').forEach((param) => {
        const [key, value] = param.split('=');
        params[key] = value;
      });
      urlTree.queryParams = params;
    }

    console.log('guestGuard: redirecting authenticated user to stored URL', urlTree.toString());
    return urlTree;
  }

  // App shell (spec §3): no onboarding gate — authed users land on HOME.
  const defaultRoute = router.createUrlTree([lang, 'app', 'home']);
  console.log('guestGuard: redirecting authenticated user to default route', defaultRoute.toString());
  return defaultRoute;
};
